'use client';

import { useSession } from 'next-auth/react';
import { useMemo } from 'react';

export interface ClientAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  userEmail?: string;
  userName?: string;
  isOnboarded?: boolean;
  tempPreferredName?: string;
  session?: any;
}

/**
 * Client-side authentication hook
 */
export function useAuth(): ClientAuthResult {
  const { data: session, status } = useSession();
  
  return useMemo(() => {
    const isLoading = status === 'loading';
    const isAuthenticated = status === 'authenticated' && !!session?.user?.email;
    
    const userEmail = isAuthenticated && session?.user ? session.user.email || undefined : undefined;
    const userName = isAuthenticated && session?.user ? session.user.name || undefined : undefined;
    const isOnboarded = isAuthenticated && session?.user ? (session.user as any).isOnboarded : undefined;
    const tempPreferredName = isAuthenticated && session?.user ? (session.user as any).tempPreferredName : undefined;

    return {
      isAuthenticated,
      isLoading,
      userEmail,
      userName,
      isOnboarded,
      tempPreferredName,
      session: isAuthenticated ? session : undefined
    };
  }, [session, status]);
}

/**
 * Hook for components that require authentication
 * Throws error if user is not authenticated
 */
export function useRequireAuth(): Required<Pick<ClientAuthResult, 'userEmail'>> & ClientAuthResult {
  const auth = useAuth();
  
  if (!auth.isLoading && !auth.isAuthenticated) {
    throw new Error('Authentication required');
  }
  
  if ( !auth.userEmail) {
    throw new Error('User ID not available in session');
  }
  
  return {
    ...auth,
    userEmail: auth.userEmail
  };
} 